import { Component, ViewChild, ViewContainerRef } from '@angular/core';
import {combineLatest, Observable} from 'rxjs';
import {map, startWith, switchMap, take, tap} from 'rxjs/operators';
import { SalesContact } from '../../atlas_sdk/api';
import { AtlasApiService } from '../../atlas_sdk/atlas.api.service';
import { BusinessNavConfigService } from '../../config.service';
import { ModalComponent } from '../../core-components/modal/modal.component';
import { SnackbarService } from '../../core-components/snackbar/snackbar.service';
import { BusinessNavDataService } from '../../data.service';
import { AtlasTranslateService } from '../../translate/translate.service';
import { UserViewType } from '../../atlas_sdk/enums';
import { format, CountryCode } from 'libphonenumber-js';

@Component({
  selector: 'atlas-sales-info-modal',
  templateUrl: './sales-info.modal.component.html',
  styleUrls: ['./sales-info.modal.component.scss']
})
export class SalesInfoModalComponent {
  public headerText$: Observable<string>;
  public prescriptionText$: Observable<string>;
  public cancelText$: Observable<string>;
  public submitText$: Observable<string>;
  public tooltipText$: Observable<string>;
  public isAdminView$: Observable<boolean>;
  public messageTxt: string;
  public sendingMessage: boolean;
  public salespersonContact$: Observable<SalesContact>;
  @ViewChild('modal', { static: true }) modal: ModalComponent;

  constructor(private viewContainerRef: ViewContainerRef, private dataService: BusinessNavDataService,
              private t: AtlasTranslateService, private apiService: AtlasApiService,
              private configService: BusinessNavConfigService, private snackbarService: SnackbarService) {
    this.headerText$ = combineLatest([
      this.dataService.partnerName$.pipe(),
      this.dataService.marketName$.pipe(startWith(null))
    ]).pipe(
      switchMap(([partner, market]) => {
        const name = market ? market : partner;
        return this.t.stream('COMMON.CONTACT_SALESPERSON', 'Contact ' + name).pipe(
          map((message: string) => message.replace('{{ salesPersonName }}', name)),
        );
      })
    );
    this.isAdminView$ = this.dataService.userViewType$.pipe(map(
      res => res === UserViewType.USER_VIEW_TYPE_ADMIN
    ));
    this.prescriptionText$ = this.t.stream('REQUEST_ASSISTANCE_MODAL.COMPOSE_MESSAGE', 'Compose Message');
    this.cancelText$ = this.t.stream('COMMON.ACTION_LABELS.CANCEL', 'Cancel');
    this.submitText$ = this.t.stream('COMMON.ACTION_LABELS.SEND', 'Send');
    this.tooltipText$ = this.t.stream('COMMON.RESULT.CANNOT_BE_IMPERSONATED', 'You cannot send a message because you are an Admin');
    this.salespersonContact$ = this.dataService.salespersonContact$;
  }

  formatPhoneNumber(phoneNumber: string | number, countryCode?: string): string {
    if (!phoneNumber || phoneNumber === undefined) {
      return '';
    }

    const numberString = phoneNumber.toString();
    if (numberString === '') {
      return '';
    }
    const formatting = 'National';
    if (countryCode as CountryCode) {
      const newCountryCode = countryCode as CountryCode;
      return format(numberString, newCountryCode, formatting);
    }
    return numberString;
  }

  closeModal(): void {
    this.modal.closeModal();
  }

  sendMessage(): void {
    if (this.sendingMessage) {
      return;
    }
    this.sendingMessage = true;
    this.configService.accountGroupId$.pipe(
      switchMap((accountGroupId: string) => this.apiService.contactUs({
        accountGroupId: accountGroupId,
        message: this.messageTxt
      })),
      take(1),
      tap(),
    ).subscribe(
      () => {
        this.snackbarService.show(this.t.instant('COMMON.RESULT.SUCCESS', 'Success'), 'OK');
        this.sendingMessage = false;
        this.closeModal();
      },
      () => {
        this.snackbarService.show(this.t.instant('COMMON.RESULT.FAILURE', 'Could not send message'), 'ERROR', {duration: 4000});
        this.sendingMessage = false;
      }
    );
  }
}
